<template>
  <div
    class="accordion__item"
    :ref="visible? 'accordionItem' : ''"
  >
    <div
      class="accordion-trigger cursor-pointer"
      :class="{'expanded': visible}"
      @click="open"
    >
      <slot name="accordion-trigger" />
      <vs-icon
        :icon="!visible ? iconMore : iconLess"
        color="#000000"
        size="24px"
      />
    </div>
    <transition
      name="accordion"
      @enter="start"
      @after-enter="end"
      @before-leave="start"
      @after-leave="end"
    >
      <div
        v-show="visible"
        class="accordion-content"
        :class="{'visible': visible}"
      >
        <slot name="accordion-content" />
      </div>
    </transition>
  </div>
</template>

<script>

export default {
  name: 'AccordionItem',
  props: {
    id: {
      type: Number,
      default: () => null
    },
    activeId: {
      type: Number,
      default: () => null,
      validator: value => value === null || value >= 0
    },
    hasChild: {
      type: Boolean,
      default: () => false
    },
    isChild: {
      type: Boolean,
      default: () => false
    },
    iconMore: {
      type: String,
      default: () => 'arrow_drop_down'
    },
    iconLess: {
      type: String,
      default: () => 'arrow_drop_up'
    }
  },
  inject: ["Accordion"],
  data() {
    return {
      index: null,
      childAccordion:
        this.hasChild
          ? {
            count: 0,
            activeIndexes: [],
            accordionMode: false
          }
          : null,
      childrenRegistered: false
    };
  },
  provide() {
    return { Accordion: this.hasChild ? this.childAccordion : this.Accordion };
  },
  computed: {
    visible() {
      const active = this.Accordion.activeIndexes;
      return Array.isArray(active)
        ? active.includes(this.index)
        : active === this.index;
    }
  },
  methods: {
    open(e) {
      if (!this.isChild) {
        e.stopPropagation()

        const wasVisible = this.visible
        // For parent items in accordion mode
        if (this.Accordion.accordionMode !== false) {
          // If this item was already open, close it
          if (wasVisible) {
            this.Accordion.activeIndexes = []
            this.$emit('closed', this.index)
            return;
          }
          // Otherwise, close all others and open this one
          this.Accordion.activeIndexes = [this.index]
          this.$emit('opened', this.index)
          return;
        }
      }

      // Default behavior for non-accordion mode or child items
      const active = this.Accordion.activeIndexes
      if (Array.isArray(active)) {
        const idx = active.indexOf(this.index)
        if (idx > -1) {
          active.splice(idx, 1)
          this.$emit('closed', this.index)
        } else {
          active.push(this.index)
          this.$emit('opened', this.index)
        }
      } else {
        this.Accordion.activeIndexes = this.visible ? [] : [this.index]
        this.$emit('closed', this.index)
      }
    },
    start(el) {
      el.style.height = `${el.scrollHeight}px`
    },
    end(el) {
      el.style.height = ""
    },
    expandAllChildren() {
      if (this.hasChild && this.childAccordion) {
        this.childAccordion.activeIndexes = Array.from(
          { length: this.childAccordion.count },
          (_, i) => this.index * 10 + i
        )
      }
    },
    registerChild() { // This method is used by this.$parent.registerChild?.()
      if (this.hasChild) {
        this.childAccordion.count++;
        this.childrenRegistered = true;
        // If parent is currently visible, expand the new child
        if (this.visible) {
          this.$nextTick(() => this.expandAllChildren());
        }
      }
    },
    scrollToItem() {
      this.$nextTick(() => {
        const element = this.$refs.accordionItem
        if (element) {
          const rect = element.getBoundingClientRect()
          const isVisible = (
            rect.top >= 0 &&
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)
          );

          if (!isVisible) {
            element.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
          }
        }
      })
    }
  },
  watch: {
    visible(newVal) {
      if (newVal && this.hasChild) {
        this.$nextTick(() => {
          if (this.index === this.activeId) {
            this.expandAllChildren()
          }

          clearTimeout(this.scrollTimeout)
          this.scrollTimeout = setTimeout(() => this.scrollToItem(), 300)
        });
      } else {
        this.$emit('closed', this.index)
      }
    },
    activeId: {
      immediate: true,
      handler(newVal) {
        if (!this.isChild && newVal !== null) {
          // Convert to array if in accordion mode
          const newActive = this.Accordion.accordionMode ? [newVal] : newVal

          // Only update if different from current state
          if (JSON.stringify(this.Accordion.activeIndexes) !== JSON.stringify(newActive)) {
            this.Accordion.activeIndexes = newActive

            // Expand children if needed
            if (this.hasChild && this.index === newVal) {
              this.$nextTick(() => this.expandAllChildren())
            }
          }
        } else if(!newVal){
          this.Accordion.activeIndexes = []
        }
      }
    }
  },
  created() {
    this.index = this.id

    if (!this.isChild && !this.Accordion.hasOwnProperty('accordionMode')) {
      this.Accordion.accordionMode = true // Only one parent open at a time
    }

    if (this.isChild) {
      this.$parent.registerChild?.()
    } else if (this.activeId !== null && this.activeId === this.index) {
      this.Accordion.activeIndexes = this.Accordion.accordionMode
        ? [this.index]
        : this.activeId;

      if (this.hasChild) {
        this.$nextTick(() => this.expandAllChildren());
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.accordion-trigger {
  @apply flex flex-row items-center justify-between;
}
</style>